<template>
    <div class="dispatch-sites-map">
        <div v-if="loading" class="loading-overlay">
            LOADING...
            <span class="loader"></span>
        </div>

        <div v-else class="dsm-content">
            <!-- Sidebar: site list with checkboxes -->
            <div class="sidebar">
                <div class="sidebar-header">
                    <div class="sidebar-tools">
                        <span class="sel-count">{{ selectedCount }} / {{ locatableSites.length }} selected</span>
                        <div class="sel-actions">
                            <button class="link-btn" @click="selectAll">All</button>
                            <button class="link-btn" @click="clearAll">None</button>
                        </div>
                    </div>
                    <input class="sidebar-search" type="text" v-model="search"
                        placeholder="Search location, device, operator, product…" />
                    <p class="hint">Pan / zoom the map to auto-select the sites in view, or tick sites
                        manually.</p>
                </div>
                <div class="site-list">
                    <div v-if="!filteredList.length" class="no-results">No sites match your search.</div>
                    <label v-for="site in filteredList" :key="site.containerId" class="site-row"
                        :class="{ selected: isSelected(site.containerId) }">
                        <input type="checkbox" :checked="isSelected(site.containerId)"
                            @change="toggleSite(site.containerId)" />
                        <span class="site-dot" :style="dotStyle(site.product)"></span>
                        <span class="site-info">
                            <span class="site-loc">{{ siteLabel(site) }}</span>
                            <span class="site-product" :style="{ color: colorFor(site.product).border }">
                                {{ site.product || 'No product' }}</span>
                            <span class="site-co">{{ site.operator || 'Unknown operator' }}</span>
                            <!-- A delivery is acceptable anywhere in this range:
                                 the low end brings the site to its minimum volume,
                                 the high end to its maximum fill level. -->
                            <span class="site-fill">Deliver now: {{ formatRange(site.minFillAmount,
                                site.fillAmount) }} L</span>
                            <span class="site-predicted">
                                Predicted{{ forecastDays ? ` in ${forecastDays}d` : '' }}:
                                {{ formatRange(site.predictedMinFillAmount,
                                    site.predictedFillAmount) }} L</span>
                            <!-- The site's own dispatch settings: Max Fill sets the
                                 delivery amount above, and the other two decide
                                 whether it starts selected. -->
                            <span class="site-limits">
                                Max fill {{ formatPct(site['max fill']) }} &middot;
                                Min fill {{ formatPct(site['minimum fill']) }}
                                <span v-if="ineligibleReason(site)" class="site-flag">
                                    {{ ineligibleReason(site) }}</span>
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            <!-- Map -->
            <div class="map-area">
                <div ref="mapContainer" class="map-canvas"></div>
                <div class="style-toggle">
                    <button class="style-btn" :class="{ active: currentStyle === 'street' }"
                        @click="setStyle('street')">Street</button>
                    <button class="style-btn" :class="{ active: currentStyle === 'satellite' }"
                        @click="setStyle('satellite')">Satellite</button>
                </div>
                <div v-if="!locatableSites.length" class="map-empty">
                    No sites with coordinates to display.
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

const PALETTE = [
    { fill: '#388bd6', border: '#7ab8f5' },
    { fill: '#2ec48c', border: '#5ddba8' },
    { fill: '#e8a838', border: '#f5c96a' },
    { fill: '#b86fc9', border: '#d6a0e3' },
    { fill: '#8d7b68', border: '#b9a795' },
    { fill: '#4fb3bf', border: '#85d6df' },
    { fill: '#c9a227', border: '#e6c65c' },
    { fill: '#7e8bd6', border: '#a6b0ec' },
];
const FALLBACK_COLOR = { fill: '#888', border: '#bbb' };

// Bright red is reserved for one meaning on this map: the site is switched off
// for dispatch. Nothing else here may use red -- the product palette above is
// deliberately red-free, and the deselected marker is marked with a neutral
// glyph rather than a red one.
const DISPATCH_OFF_COLOR = { fill: '#ff1f1f', border: '#ff6b6b' };

const STYLES = {
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    street: 'mapbox://styles/mapbox/streets-v12',
};

export default {
    name: 'DispatchSitesMap',
    data() {
        return {
            map: null,
            markers: new Map(), // containerId -> { marker, el }
            currentStyle: 'satellite',
            search: '',
            loading: true,
            selectedMap: {}, // containerId -> true
            hadInitialSelection: false,
        };
    },
    computed: {
        sites() {
            return this.$store.getters['dispatch/getFilteredContainersWithCoords'];
        },
        locatableSites() {
            return this.sites.filter((s) => this.hasCoords(s));
        },
        filteredList() {
            const q = this.search.trim().toLowerCase();
            if (!q) return this.locatableSites;
            return this.locatableSites.filter((s) =>
                [this.siteLabel(s), this.deviceLabel(s), s.operator, s.product]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase()
                    .includes(q)
            );
        },
        selectedCount() {
            return this.locatableSites.filter((s) => this.isSelected(s.containerId)).length;
        },
        // Forecast horizon for the predicted fill amount (days to the dispatch date).
        forecastDays() {
            return this.$store.getters['dispatch/getDispatchForecastDays'];
        },
        products() {
            const set = new Set();
            this.locatableSites.forEach((s) => set.add(s.product || 'No product'));
            return Array.from(set).sort();
        },
        productColorMap() {
            const map = {};
            this.products.forEach((p, i) => {
                map[p] = PALETTE[i % PALETTE.length];
            });
            return map;
        },
    },
    async mounted() {
        await this.loadData();
        this.loading = false;
        // Seed selection from any existing dispatch selection.
        const saved = this.$store.getters['dispatch/getCurrentDispatchSites'] || [];
        const seed = {};
        if (saved.length) {
            saved.forEach((s) => {
                seed[s.containerId] = true;
            });
        } else {
            // Nothing selected yet, so start from every eligible site. A site
            // switched off for dispatch, or short of its own Minimum Fill, is
            // still listed -- it is just left unticked for a planner to opt in
            // deliberately.
            this.locatableSites.forEach((s) => {
                if (this.isEligible(s)) seed[s.containerId] = true;
            });
        }
        this.hadInitialSelection = saved.length > 0;
        // Through applySelection so a saved dispatch holding a site that has
        // since been switched off is cleaned up, and so a default selection is
        // written back -- otherwise the step count and the later wizard steps
        // would still see nothing selected.
        this.applySelection(seed);
        this.$nextTick(() => this.initMap());
    },
    unmounted() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },
    methods: {
        async loadData() {
            // Sites need container-status (for the filter) and the containers
            // module (for coordinates).
            try {
                await Promise.all([
                    this.$store.dispatch('dispatch/loadContainersStatus', { typeId: 1 }),
                    this.$store.dispatch('containers/loadContainers'),
                ]);
            } catch (error) {
                console.warn('dispatch-sites-map: failed to load data.', error);
            }
        },
        hasCoords(s) {
            const lat = parseFloat(s.latitude);
            const lng = parseFloat(s.longitude);
            return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
        },
        colorFor(product) {
            return this.productColorMap[product || 'No product'] || FALLBACK_COLOR;
        },
        dotStyle(product) {
            const c = this.colorFor(product);
            return { background: c.fill, borderColor: c.border };
        },
        siteLabel(s) {
            return s.surface || s.alias || `Site ${s.containerId}`;
        },
        deviceLabel(s) {
            if (s.devices) return `Device ${s.devices}`;
            if (s.serialnum) return `Device #${s.serialnum}`;
            return `Container ${s.containerId}`;
        },
        formatUllage(value) {
            return Math.round(Number(value) || 0).toLocaleString();
        },
        // The delivery range. Collapses to a single figure when the ends match
        // -- which is the common case for a site with no minimum level set, so
        // "0 - 191" does not appear where "191" says the same thing.
        formatRange(minValue, maxValue) {
            const min = Math.round(Number(minValue) || 0);
            const max = Math.round(Number(maxValue) || 0);
            if (min <= 0) return max.toLocaleString();
            if (min === max) return max.toLocaleString();
            return min.toLocaleString() + '–' + max.toLocaleString();
        },
        isSelected(id) {
            return !!this.selectedMap[id];
        },
        // Dispatch Enabled is the only thing that keeps a site out of a run.
        // Minimum Fill is no longer an eligibility test -- it is the bottom of
        // the site's delivery range. Undefined means "no reason to exclude".
        isEligible(site) {
            return site.dispatchEnabled !== false;
        },
        // Why a site is not selected by default, or '' when it is eligible.
        ineligibleReason(site) {
            return site.dispatchEnabled === false ? 'Dispatch off' : '';
        },
        formatPct(value) {
            const n = Number(value);
            return value == null || value === '' || isNaN(n) ? '—' : n + '%';
        },
        initMap() {
            const container = this.$refs.mapContainer;
            if (!container) return;

            mapboxgl.accessToken = this.$store.state.config.mapbox.accessToken;
            const cfgCenter = this.$store.state.config.mapbox.defaultCenter || [53.5461, -113.4937];

            this.map = new mapboxgl.Map({
                container,
                style: STYLES[this.currentStyle],
                center: [cfgCenter[1], cfgCenter[0]],
                zoom: this.$store.state.config.mapbox.defaultZoom || 5,
                attributionControl: true,
            });
            this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
            this.map.addControl(new mapboxgl.ScaleControl({ maxWidth: 140, unit: 'metric' }), 'bottom-right');

            // Keep the canvas sized to its container (window resize, layout changes).
            this.resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(() => {
                    if (this.map) this.map.resize();
                });
            });
            this.resizeObserver.observe(container);

            this.map.on('load', () => {
                this.addMarkers();
                this.fitToSites();
                // After the initial (programmatic) fit settles, decide the initial
                // selection, then start tracking user-driven map moves.
                this.map.once('idle', () => {
                    if (!this.hadInitialSelection) {
                        this.selectInBounds();
                    } else {
                        // Already cleaned in mounted(); re-applied so every path
                        // to a selection runs through the same guard.
                        this.applySelection(this.selectedMap);
                    }
                    this.map.on('moveend', this.onUserMove);
                });
            });
        },
        addMarkers() {
            if (!this.map) return;
            this.markers = new Map();
            this.locatableSites.forEach((site) => {
                const el = document.createElement('div');
                el.className = 'dsm-marker';
                el.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    this.toggleSite(site.containerId);
                });
                const marker = new mapboxgl.Marker({ element: el })
                    .setLngLat([parseFloat(site.longitude), parseFloat(site.latitude)])
                    .addTo(this.map);
                this.markers.set(site.containerId, { marker, el, site });
            });
            this.refreshMarkerStyles();
        },
        refreshMarkerStyles() {
            this.markers.forEach((entry) => {
                const selected = this.isSelected(entry.site.containerId);
                const dispatchOff = entry.site.dispatchEnabled === false;
                // A site switched off for dispatch is bright red, overriding its
                // product colour -- red means only this. Everything else keeps
                // its product colour in both states, with a neutral glyph when
                // excluded rather than being greyed out.
                const c = dispatchOff ? DISPATCH_OFF_COLOR : this.colorFor(entry.site.product);
                entry.el.style.background = c.fill;
                entry.el.style.borderColor = c.border;
                entry.el.classList.toggle('selected', selected);
                entry.el.classList.toggle('deselected', !selected);
                entry.el.classList.toggle('dispatch-off', dispatchOff);
            });
        },
        // Every selection change goes through here, so a dispatch-disabled site
        // can never end up selected -- whether it came from the viewport sweep,
        // the All button, a manual tick on the bar or the map, or a saved
        // dispatch made before the site was switched off. Ids that are not
        // locatable are dropped too, matching what persist() sends.
        applySelection(next) {
            const cleaned = {};
            this.locatableSites.forEach((site) => {
                if (next[site.containerId] && site.dispatchEnabled !== false) {
                    cleaned[site.containerId] = true;
                }
            });
            this.selectedMap = cleaned;
            this.refreshMarkerStyles();
            this.persist();
        },
        // Selection follows the viewport: sites inside the current map bounds are
        // selected, sites outside are unselected.
        selectInBounds() {
            if (!this.map) return;
            const bounds = this.map.getBounds();
            const next = {};
            this.locatableSites.forEach((s) => {
                if (bounds.contains([parseFloat(s.longitude), parseFloat(s.latitude)])) {
                    next[s.containerId] = true;
                }
            });
            this.applySelection(next);
        },
        onUserMove() {
            this.selectInBounds();
        },
        toggleSite(id) {
            const next = { ...this.selectedMap };
            if (next[id]) {
                delete next[id];
            } else {
                next[id] = true;
            }
            this.applySelection(next);
        },
        selectAll() {
            const next = {};
            this.locatableSites.forEach((s) => {
                next[s.containerId] = true;
            });
            this.applySelection(next);
        },
        clearAll() {
            this.applySelection({});
        },
        persist() {
            const selected = this.locatableSites.filter((s) => this.isSelected(s.containerId));
            this.$store.dispatch('dispatch/setSelectedSites', selected);
        },
        fitToSites() {
            if (!this.map || !this.locatableSites.length) return;
            if (this.locatableSites.length === 1) {
                const s = this.locatableSites[0];
                this.map.flyTo({ center: [parseFloat(s.longitude), parseFloat(s.latitude)], zoom: 11 });
                return;
            }
            const bounds = new mapboxgl.LngLatBounds();
            this.locatableSites.forEach((s) =>
                bounds.extend([parseFloat(s.longitude), parseFloat(s.latitude)])
            );
            this.map.fitBounds(bounds, { padding: 60, maxZoom: 12, duration: 0 });
        },
        setStyle(style) {
            if (style === this.currentStyle || !this.map) return;
            this.currentStyle = style;
            this.markers.forEach(({ marker }) => marker.remove());
            this.markers = new Map();
            this.map.setStyle(STYLES[style]);
            this.map.once('style.load', () => this.addMarkers());
        },
    },
};
</script>

<style scoped>
.dispatch-sites-map {
    margin: 0 20px;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
}

.dsm-content {
    display: flex;
    flex: 1;
    min-height: 0;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    overflow: hidden;
}

/* Sidebar */
.sidebar {
    width: 300px;
    flex-shrink: 0;
    background: #16222f;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.sidebar-header {
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    flex-shrink: 0;
}

.sidebar-tools {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.sel-count {
    font-size: 12px;
    color: rgb(255, 140, 40);
    font-weight: 600;
}

.sel-actions {
    display: flex;
    gap: 6px;
}

.link-btn {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 5px;
    padding: 2px 9px;
    font-size: 11px;
    cursor: pointer;
}

.link-btn:hover {
    background: rgba(255, 255, 255, 0.16);
}

.sidebar-search {
    width: 100%;
    padding: 7px 10px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    color: #fff;
    font-size: 13px;
    outline: none;
}

.sidebar-search::placeholder {
    color: rgba(255, 255, 255, 0.3);
}

.hint {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.4);
    margin: 8px 0 0 0;
    line-height: 1.4;
}

.site-list {
    overflow-y: auto;
    flex: 1;
}

.site-row {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    cursor: pointer;
}

.site-row:hover {
    background: rgba(255, 255, 255, 0.05);
}

.site-row.selected {
    background: rgba(255, 115, 0, 0.08);
}

.site-row input {
    margin-top: 3px;
    cursor: pointer;
    flex-shrink: 0;
}

.site-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid;
    flex-shrink: 0;
    margin-top: 3px;
}

.site-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.site-loc {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.site-product {
    font-size: 12px;
    font-weight: 600;
    margin-top: 2px;
}

.site-co {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
    margin-top: 2px;
}

.site-fill {
    font-size: 11px;
    color: #e8a838;
    margin-top: 2px;
}

.site-limits {
    font-size: 11px;
    color: rgba(150, 150, 150, 0.85);
}

/* Says why a listed site is not ticked by default. */
.site-flag {
    margin-left: 6px;
    padding: 0 5px;
    border: 1px solid rgba(255, 140, 40, 0.6);
    border-radius: 8px;
    color: rgb(255, 140, 40);
    white-space: nowrap;
}

.site-predicted {
    font-size: 11px;
    color: #5ddba8;
    margin-top: 1px;
}

.no-results {
    padding: 24px 14px;
    color: rgba(255, 255, 255, 0.3);
    font-size: 13px;
    text-align: center;
}

/* Map */
.map-area {
    flex: 1;
    position: relative;
    background: #111c27;
}

.map-canvas {
    position: absolute;
    inset: 0;
}

.style-toggle {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 5;
    display: flex;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.style-btn {
    padding: 7px 13px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: none;
}

.style-btn.active {
    background: #1e2d3d;
    color: #fff;
}

.style-btn:not(.active) {
    background: rgba(255, 255, 255, 0.85);
    color: #1e2d3d;
}

.map-empty {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    background: rgba(30, 45, 61, 0.85);
    padding: 16px 24px;
    border-radius: 8px;
}

@media (max-width: 640px) {
    .dsm-content {
        flex: none;
        flex-direction: column;
        height: auto;
    }

    .sidebar {
        width: 100%;
        height: 220px;
        order: 2;
        border-right: none;
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }

    .map-area {
        order: 1;
        height: 50vh;
    }
}
</style>

<!-- Marker styling (rendered outside the scoped component DOM). -->
<style>
.dsm-marker {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 3px solid;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25), 0 2px 6px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    transition: transform 0.12s;
}

.dsm-marker:hover {
    transform: scale(1.3);
}

.dsm-marker.selected {
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5), 0 2px 8px rgba(0, 0, 0, 0.6);
}

/* Switched off for dispatch. The fill/border come from DISPATCH_OFF_COLOR in
   the script; this adds a red halo so it reads at a glance even among many
   pins. The only red on this map. */
.dsm-marker.dispatch-off {
    box-shadow: 0 0 0 3px rgba(255, 31, 31, 0.45), 0 2px 8px rgba(0, 0, 0, 0.6);
}

/* Excluded site: keep the marker colour, mark it with a neutral ✕. Red is
   reserved for dispatch-disabled sites, so it cannot be used here. */
.dsm-marker.deselected::after {
    content: '✕';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -52%);
    color: rgba(255, 255, 255, 0.95);
    font-size: 18px;
    font-weight: 900;
    line-height: 1;
    text-shadow: 0 0 2px rgba(0, 0, 0, 0.9);
    pointer-events: none;
}
</style>
